import { dupMode } from './DuplicatePanel'
import { compareMode } from '../compareSpec'
import { n } from '../format'

/**
 * The counts band above the table: how many rows you are looking at, and every
 * other number that explains how it got that small.
 *
 * Every figure here is reported by the server — none is inferred except the two
 * marked below, which are subtractions of two server figures. While a scan is
 * running the numbers are still climbing, so the band says so rather than
 * presenting a half-finished count as final.
 */

function Tile({ value, label, hint, kind = '', pending }) {
  return (
    <div className={`count-tile ${kind}`} title={hint ?? undefined}>
      <b>{value}</b>
      <span>
        {label}
        {pending && <i className="count-live" aria-label="still counting" />}
      </span>
    </div>
  )
}

/**
 * @param filtered whether filters are actually running. Not taken from the
 *   status: the duplicates endpoint reports `filtered: true` unconditionally,
 *   which would have the band claim filters matched when none are set.
 */
/**
 * The counts for a comparison. The lead is the number the question was actually
 * about — how many differences there are — and it is a sum of three server
 * figures rather than a fourth figure, because "different" is exactly "the file
 * changed it, dropped it, or added it".
 *
 * Repeated keys are called out separately whenever there are any: they are rows
 * the comparison could not pair, so folding them into a difference count would
 * overstate what is known.
 */
function compareTiles(status, compareApplied, scanning) {
  const mode = compareMode(compareApplied.mode)
  const changed = status.changed ?? 0
  const missing = status.missing ?? 0
  const extra = status.extra ?? 0
  const repeats = (status.repeatView ?? 0) + (status.repeatFile ?? 0)
  const differences = changed + missing + extra
  const tiles = []

  tiles.push({
    value: n(differences),
    label: differences === 1 ? 'difference' : 'differences',
    kind: 'lead',
    hint: 'Rows the uploaded file changes, leaves out, or adds — the three tiles beside this one, added up',
    pending: scanning,
  })
  // Every mode except "everything" is a slice, so what is on screen is a
  // different number from the headline and has to say so.
  if (compareApplied.mode !== 'all') {
    tiles.push({
      value: n(status.available ?? 0),
      label: `${mode.scope} listed below`,
      hint: `${mode.blurb} — this is what an export writes`,
      pending: scanning,
    })
  }
  tiles.push({
    value: n(changed),
    label: 'values differ',
    kind: 'flag',
    hint: 'Rows on both sides whose compared columns disagree',
    pending: scanning,
  })
  tiles.push({
    value: n(missing),
    label: 'missing from the file',
    kind: 'cut',
    hint: 'Rows in your view that the uploaded file does not have at all',
    pending: scanning,
  })
  tiles.push({
    value: n(extra),
    label: 'only in the file',
    kind: 'merge',
    hint: 'Rows in the uploaded file with no counterpart in your view',
    pending: scanning,
  })
  tiles.push({
    value: n(status.same ?? 0),
    label: 'unchanged',
    hint: 'Rows that match on the key and agree on every compared column',
    pending: scanning,
  })
  if (repeats > 0) {
    tiles.push({
      value: n(repeats),
      label: 'keys repeat',
      kind: 'flag',
      hint: `${n(status.repeatView ?? 0)} in your view and ${n(status.repeatFile ?? 0)} in the uploaded file. A repeated key cannot be paired, so these rows are flagged rather than compared — the Duplicates tool is how to resolve them.`,
      pending: scanning,
    })
  }
  tiles.push({
    value: n(status.viewRows ?? 0),
    label: 'rows in your view',
    hint: 'What the table was showing before the comparison — the base every verdict is relative to',
    pending: scanning,
  })
  tiles.push({
    value: n(status.fileRows ?? 0),
    label: 'rows in the file',
    hint: status.fileName ?? 'The uploaded file',
    pending: scanning,
  })
  return tiles
}

export function TableStats({ dataset, status, dupApplied, compareApplied, insightApplied, filtered }) {
  const scanning = !status.done
  const mode = dupApplied ? dupMode(dupApplied.mode) : null
  const total = dataset.indexed && dataset.rows > 0 ? dataset.rows : null
  const tiles = []

  if (compareApplied) {
    // A comparison is about the difference between two files, so its own counts
    // replace the row counts entirely — those are what `rows in your view` is.
    tiles.push(...compareTiles(status, compareApplied, scanning))
  } else if (!dupApplied) {
    tiles.push({
      value: n(status.matched),
      label: filtered ? 'rows match' : 'rows',
      kind: 'lead',
      // No duplicate key is set, so nothing has been folded together: every row
      // counts as its own record, repeats included. Worth saying, because it is
      // the one configuration where the headline number counts duplicates.
      hint: 'Rows an export writes. No duplicate key is set, so repeated records are still counted separately — open Duplicates to fold them together.',
      pending: scanning,
    })
    if (total !== null) {
      tiles.push({ value: n(total), label: 'rows in the file', hint: dataset.path })
      if (filtered && status.done) {
        // Subtraction of two server figures, both final once the scan is done.
        tiles.push({
          value: n(Math.max(0, total - status.matched)),
          label: 'filtered out',
          kind: 'cut',
          hint: 'Rows in the file that your filters exclude',
        })
      }
    }
  } else {
    // In scope = the rows the filters selected, which the duplicate pass ran over.
    // The server reports it as `scanned`; it is the whole file when nothing filters.
    const inScope = status.scanned ?? 0
    const dupRows = status.duplicateRows ?? 0
    const groups = status.groups ?? 0
    const key = dupApplied.columns.join(' + ')
    // One row per record: every row whose key never repeats, plus one surviving
    // copy of each key that does. Two server figures subtracted and added — the
    // same number `dedupe` mode reports as its own row count.
    const cleanRecords = Math.max(0, inScope - dupRows) + groups

    // Three of the modes *are* an outcome, so their own row count is the final
    // number. The other two are review views — you are looking at the copies, or
    // at what a dedupe would throw away — so the count that matters is still the
    // record count that configuration produces, and the view count comes second.
    const outcome = { dedupe: 'clean records', kept: 'records kept', unique: 'records that never repeat' }
    const isOutcome = !!outcome[dupApplied.mode]

    tiles.push({
      value: n(isOutcome ? status.matched : cleanRecords),
      label: isOutcome ? outcome[dupApplied.mode] : 'clean records',
      kind: 'lead',
      hint: isOutcome
        ? `${mode.blurb} — this is what an export writes`
        : `One row per record, if you hid the duplicates. The table below is listing ${mode.scope} so you can check the verdicts first.`,
      pending: scanning,
    })
    if (!isOutcome) {
      tiles.push({
        value: n(status.matched),
        label: `${mode.scope} listed below`,
        hint: `${mode.blurb} — this is what an export writes`,
        pending: scanning,
      })
    }
    tiles.push({
      value: n(inScope),
      label: filtered ? 'rows match your filters' : 'rows in scope',
      hint: filtered
        ? 'Rows your filters selected — what the duplicate pass ran over'
        : 'The whole file, since nothing is filtered — what the duplicate pass ran over',
      pending: scanning,
    })
    tiles.push({
      value: n(groups),
      label: `repeated ${key}`,
      hint: `Distinct values of ${key} that appear on more than one row`,
      pending: scanning,
    })
    tiles.push({
      value: n(dupRows),
      label: 'rows are copies',
      hint: 'Every row belonging to a value that repeats, counting all copies',
      pending: scanning,
    })
    if (status.removed > 0) {
      tiles.push({
        value: `−${n(status.removed)}`,
        label: 'removed here',
        kind: 'cut',
        hint: 'Rows this mode drops from the rows the filters selected',
        pending: scanning,
      })
    }
    if (status.resolveRules > 0) {
      tiles.push({
        value: n(status.resolveRules),
        label: status.merged ? 'rules build each record' : 'rules pick the copy',
        kind: status.merged ? 'merge' : '',
        hint: status.merged
          ? 'Each record is assembled field by field using these rules'
          : 'These rules decide which copy of a repeated key survives',
      })
    }
  }

  if (insightApplied) {
    tiles.push({
      value: n(insightApplied.columns.length),
      label: insightApplied.columns.length === 1 ? 'column counted' : 'columns counted',
      kind: 'merge',
      hint: insightApplied.columns.join(', '),
    })
  }

  return (
    <div className="counts">
      {tiles.map((t) => (
        <Tile key={t.label} {...t} />
      ))}
    </div>
  )
}
